import { useEffect, useRef, useState } from 'react';
import {
  Badge, Banner, BlockStack, Box, DataTable, Divider, InlineGrid, InlineStack,
  Modal, SkeletonBodyText, Text,
} from '@shopify/polaris';
import apiClient from '../utils/api';

const when = (v) => (v ? new Date(v).toLocaleString(undefined, {
  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
}) : '—');
const day = (v) => (v ? new Date(v).toLocaleDateString(undefined, {
  month: 'short', day: 'numeric', year: 'numeric',
}) : '—');
const money = (v, ccy) => (v == null ? '—' : apiClient.formatCurrency(v, ccy || undefined));
const or = (v) => (v == null || v === '' ? '—' : String(v));

/** Label above value, which is how every figure here reads: a fact and what it is. */
function Field({ label, children }) {
  return (
    <BlockStack gap="050">
      <Text variant="bodySm" tone="subdued">{label}</Text>
      {typeof children === 'string' || typeof children === 'number'
        ? <Text variant="bodySm">{String(children)}</Text>
        : children}
    </BlockStack>
  );
}

function Section({ title, note, children }) {
  return (
    <BlockStack gap="200">
      <BlockStack gap="050">
        <Text variant="headingSm" as="h3">{title}</Text>
        {note && <Text variant="bodySm" tone="subdued">{note}</Text>}
      </BlockStack>
      {children}
      <Divider />
    </BlockStack>
  );
}

/** A postal address as the retailer sent it, in the order someone reads an address. */
function Address({ a }) {
  if (!a) return <Text variant="bodySm" tone="subdued">Not sent by the retailer</Text>;
  const name = [a.firstname, a.lastname].filter(Boolean).join(' ');
  const lines = [
    name,
    a.company,
    a.company_2,
    a.street_1,
    a.street_2,
    [a.city, a.state, a.zip_code].filter(Boolean).join(', '),
    a.country || a.country_iso_code,
    a.additional_info,
  ].filter(Boolean);
  return (
    <BlockStack gap="025">
      {lines.map((l, i) => <Text key={`${i}-${l}`} variant="bodySm">{l}</Text>)}
      {a.phone && <Text variant="bodySm" tone="subdued">{a.phone}</Text>}
    </BlockStack>
  );
}

const BRAND_TONE = { attributes: 'success', unconnected: 'warning', none: 'critical' };

/**
 * The stored state as a plain sentence. Same words as the Error column on the table behind
 * this dialog, so the two never read as two different facts about the same order.
 */
const MATCH_RESULT = {
  ATTRIBUTED: { label: 'Matched', tone: undefined },
  PARTIAL: { label: 'Some lines have no brand', tone: 'critical' },
  UNATTRIBUTED: { label: 'No brand matched', tone: 'critical' },
  SKIPPED: { label: 'Canceled or refused', tone: undefined },
  PENDING: { label: 'Not checked yet', tone: undefined },
};

/** The per-line brand cell. Same rule and same tones as the table's Brand column. */
function LineBrand({ brand }) {
  if (!brand) return <Text variant="bodySm" tone="subdued">—</Text>;
  if (brand.state === 'unknown-sku') {
    // Distinct from "no brand": the SKU is not in this account's map, so no brand name exists
    // to resolve yet. The fix is an offer fetch, not a connection.
    return <Badge>SKU not in the map</Badge>;
  }
  return (
    <Badge tone={BRAND_TONE[brand.state] || undefined}>
      {brand.brandName || brand.retailerName || '—'}
    </Badge>
  );
}

/**
 * Everything stored for one retailer order.
 *
 * Reads the stored payload through its own endpoint rather than the list, so opening a row
 * costs no Mirakl rate limit and shows exactly what the brand matching had to work from.
 *
 * The customer's name, contact email and both addresses are shown deliberately: this is the
 * only screen in 2W where the retailer's own order detail is visible, and reconciling a
 * dispute needs them. They are the retailer's customer, not ours.
 */
export default function RetailerOrderDetailModal({ marketplaceId, miraklOrderId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Guards a slow response for an order the operator has already navigated away from landing
  // in the dialog for a different one.
  const seq = useRef(0);

  useEffect(() => {
    if (!marketplaceId || !miraklOrderId) return;
    const mine = ++seq.current;
    setLoading(true);
    setError('');
    setDetail(null);
    apiClient.getStoredRetailerOrderDetail(marketplaceId, miraklOrderId)
      .then((d) => { if (mine === seq.current) setDetail(d); })
      .catch((e) => { if (mine === seq.current) setError(e.message || 'Could not load this order'); })
      .finally(() => { if (mine === seq.current) setLoading(false); });
  }, [marketplaceId, miraklOrderId]);

  const o = detail?.order;
  const ccy = o?.currencyCode;
  const ship = detail?.retailerShipment;
  const retailerHasTracking = Boolean(ship?.trackingNumber);

  return (
    <Modal
      open
      onClose={onClose}
      title={`Retailer order ${miraklOrderId}`}
      size="large"
      secondaryActions={[{ content: 'Close', onAction: onClose }]}
    >
      <Modal.Section>
        {loading && <SkeletonBodyText lines={8} />}
        {error && <Banner tone="critical">{error}</Banner>}

        {detail && (
          <BlockStack gap="400">
            {/* GATED ON THE ORDER ITSELF, not on `detail`.
            
                Every field below dereferences `o` without a guard, while the block above
                checks only that `detail` arrived — so a payload carrying slices but no order
                row threw during render and blanked the whole dialog. Gating this section
                alone rather than the whole stack: the lines, the shipment, the attribution
                and the customer do not depend on it and are still worth showing. */}
            {o && (
            <Section
              title="Order"
              note="As the retailer sent it. Every figure here is theirs, for the whole order — not a billing figure."
            >
              <InlineGrid columns={{ xs: 2, md: 4 }} gap="300">
                <Field label="Retailer order id">{o.miraklOrderId}</Field>
                <Field label="Retailer status"><Badge>{or(o.miraklStatus)}</Badge></Field>
                <Field label="Customer reference">{or(o.customerReference)}</Field>
                <Field label="Commercial id">{or(o.commercialId)}</Field>
                <Field label="Order date">{when(o.createdDate)}</Field>
                <Field label="Last changed">{when(o.lastUpdatedDate)}</Field>
                <Field label="Ship-by deadline">{when(o.shippingDeadline)}</Field>
                <Field label="Fetched by 2W">{when(o.fetchedAt)}</Field>
                <Field label="Order total">{money(o.totalPrice, ccy)}</Field>
                <Field label="Retailer commission">{money(o.totalCommission, ccy)}</Field>
                <Field label="Shipping charged">{money(o.shippingPrice, ccy)}</Field>
                <Field label="Tax mode">{or(o.taxMode)}</Field>
              </InlineGrid>
              {(o.fullyRefunded || o.hasIncident) && (
                <InlineStack gap="200">
                  {o.fullyRefunded && <Badge tone="warning">Fully refunded</Badge>}
                  {o.hasIncident && <Badge tone="critical">Has an incident</Badge>}
                </InlineStack>
              )}
            </Section>
            )}

            <Section
              title="Lines"
              note="Brand is worked out per line: the line's SKU is looked up in this account's SKU map to get the name the retailer files it under, and that name is matched against your brands."
            >
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'numeric', 'numeric', 'numeric', 'text']}
                headings={['SKU', 'Product', 'Brand', 'Qty', 'Unit price', 'Line total', 'Line state']}
                rows={(detail.lines || []).map((l) => [
                  or(l.offerSku),
                  or(l.productTitle),
                  <LineBrand key={`b-${l.orderLineId}`} brand={l.brand} />,
                  or(l.quantity),
                  money(l.unitPrice, ccy),
                  money(l.totalPrice, ccy),
                  <BlockStack gap="025" key={`s-${l.orderLineId}`}>
                    <Text variant="bodySm">{or(l.lineState)}</Text>
                    {l.shippedDate && (
                      <Text variant="bodySm" tone="subdued">{`shipped ${day(l.shippedDate)}`}</Text>
                    )}
                    {l.receivedDate && (
                      <Text variant="bodySm" tone="subdued">{`received ${day(l.receivedDate)}`}</Text>
                    )}
                  </BlockStack>,
                ])}
              />
            </Section>

            {/* The caption that stood here explained that this tracking is the retailer's
                rather than ours, and that a push from us would be redundant. The heading
                already says whose shipment it is, and the banner below says the order was
                already shipped at the retailer — so it was a third telling of the same
                fact, above the two that say it in place. */}
            <Section title="Retailer's own shipment">
              {retailerHasTracking ? (
                <Banner tone="info">
                  {`Already shipped at the retailer — ${ship.carrierName || ship.carrierCode || 'carrier unknown'}, tracking ${ship.trackingNumber}.`}
                </Banner>
              ) : (
                <Text variant="bodySm" tone="subdued">
                  The retailer holds no tracking for this order.
                </Text>
              )}
              <InlineGrid columns={{ xs: 2, md: 4 }} gap="300">
                <Field label="Tracking number">{or(ship?.trackingNumber)}</Field>
                <Field label="Carrier">{or(ship?.carrierName || ship?.carrierCode)}</Field>
                <Field label="Shipping type">{or(ship?.shippingTypeLabel)}</Field>
                <Field label="Zone">{or(ship?.shippingZoneLabel)}</Field>
                <Field label="Delivery from">{day(ship?.deliveryEarliest)}</Field>
                <Field label="Delivery by">{day(ship?.deliveryLatest)}</Field>
              </InlineGrid>
            </Section>

            <Section
              title="Customer"
              note="The retailer's customer, as they sent them. Shown here for reconciling a dispute; 2W attaches no customer to the Shopify order it creates."
            >
              <InlineGrid columns={{ xs: 1, md: 3 }} gap="300">
                <Field label="Name">
                  {[detail.customer?.firstName, detail.customer?.lastName].filter(Boolean).join(' ') || '—'}
                </Field>
                <Field label="Contact email">{or(detail.customer?.notificationEmail)}</Field>
                <Field label="Retailer customer id">{or(detail.customer?.customerId)}</Field>
                <Field label="Shipping address">
                  <Address a={detail.customer?.shippingAddress} />
                </Field>
                <Field label="Billing address">
                  <Address a={detail.customer?.billingAddress} />
                </Field>
              </InlineGrid>
            </Section>

            <Section
              title="Brand matching"
              note="Whether this order reached a brand, from stored data. Match orders to brands on the table checks it again — no re-fetch needed."
            >
              <InlineGrid columns={{ xs: 2, md: 4 }} gap="300">
                <Field label="Result">
                  <Badge tone={MATCH_RESULT[detail.attribution?.state]?.tone}>
                    {MATCH_RESULT[detail.attribution?.state]?.label || or(detail.attribution?.state)}
                  </Badge>
                </Field>
                <Field label="Lines matched">
                  {`${detail.attribution?.linesAttributed ?? 0} of ${detail.attribution?.linesTotal ?? 0}`}
                </Field>
                <Field label="Brands matched">{or(detail.attribution?.brandsMatched)}</Field>
                <Field label="Last checked">{when(detail.attribution?.attributedAt)}</Field>
              </InlineGrid>
              {detail.attribution?.note && (
                <Banner tone="warning">{detail.attribution.note}</Banner>
              )}
              {detail.attribution?.unknownSkus?.length > 0 && (
                <Field label="SKUs not in this account's map">
                  <Text variant="bodySm">{detail.attribution.unknownSkus.join(', ')}</Text>
                </Field>
              )}
              {detail.attribution?.unmatchedBrands?.length > 0 && (
                <Field label="Retailer brand names that matched no connection">
                  <Text variant="bodySm">{detail.attribution.unmatchedBrands.join(', ')}</Text>
                </Field>
              )}
            </Section>

            <Section
              title="Orders to Shopify"
              note="The per-brand rows the matching produced. None means no brand received this order — not that a Shopify order failed."
            >
              {(detail.slices || []).length === 0 ? (
                <Text variant="bodySm" tone="subdued">
                  No brand row exists for this order yet.
                </Text>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'numeric', 'text']}
                  headings={['Brand', 'Our status', 'Shopify order', 'Slice total', 'Progress']}
                  rows={detail.slices.map((s) => [
                    or(s.brandName),
                    <Badge key={`st-${s.id}`} tone={s.status === 'FAILED' ? 'critical' : undefined}>
                      {or(s.status)}
                    </Badge>,
                    or(s.shopifyOrderId || s.shopifyOrderGid),
                    money(s.totalPrice, ccy),
                    <BlockStack gap="025" key={`p-${s.id}`}>
                      {s.acceptedAt && <Text variant="bodySm">{`accepted ${day(s.acceptedAt)}`}</Text>}
                      {s.rejectedAt && <Text variant="bodySm">{`rejected ${day(s.rejectedAt)}`}</Text>}
                      {s.trackingPushedAt && <Text variant="bodySm">{`tracking sent ${day(s.trackingPushedAt)}`}</Text>}
                      {s.shipValidatedAt && <Text variant="bodySm">{`shipment validated ${day(s.shipValidatedAt)}`}</Text>}
                      {s.error && <Text variant="bodySm" tone="critical">{s.error}</Text>}
                    </BlockStack>,
                  ])}
                />
              )}
            </Section>

            <Box paddingBlockStart="200">
              <Text variant="bodySm" tone="subdued">
                Everything above is read from what 2W stored at the last fetch. Nothing here
                calls the retailer.
              </Text>
            </Box>
          </BlockStack>
        )}
      </Modal.Section>
    </Modal>
  );
}
